import apiInstance from '@/api';
import toastMessage from '@/components/Toast';
import { ThunkApiConfig } from '@/models/ThunkApiConfig';
import { getThunkError } from '@/store/utility/thunkErrorUtils';
import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  donwloadTerms,
  pendingTermsAgreement,
  pendingTermsAgreementCount,
  termsAgreementAccept,
  termsLists,
} from './actions';
import { DownloadS3UrlTerms, TermsAgreementConfirmPayload } from './types';

export const getTermsAgreementCount =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'terms/getTermsAgreementCount',
    async (_, { dispatch, rejectWithValue }) => {
      dispatch(pendingTermsAgreementCount.request());
      try {
        const response = await apiInstance.terms.getTermsAgreementCount();
        dispatch(
          pendingTermsAgreementCount.success({ data: response.data.data }),
        );
      } catch (error: any) {
        dispatch(pendingTermsAgreementCount.failure());
        const message = getThunkError(error);
        toastMessage.error(`Failed to load terms: ${message}`);
        return rejectWithValue(
          error.message || 'An error occurred while fetching terms.',
        );
      }
    },
  );

export const getPendingTermsAgreement =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'terms/getPendingTermsAgreement',
    async (_, { dispatch, rejectWithValue }) => {
      dispatch(pendingTermsAgreement.request());
      try {
        const response = await apiInstance.terms.getTermsAgreement();
        const data = response.data.data;
        if (Array.isArray(data) && data.length === 0) {
          await dispatch(getTermsAgreementCount());
        }

        dispatch(pendingTermsAgreement.success({ data }));
      } catch (error: any) {
        dispatch(pendingTermsAgreement.failure());
        const message = getThunkError(error);
        toastMessage.error(`Failed to load terms: ${message}`);
        return rejectWithValue(
          error.message || 'An error occurred while fetching terms.',
        );
      }
    },
  );

export const termsAggrementConfirm =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'terms/termsAggrementConfirm',
    async (
      { id }: TermsAgreementConfirmPayload,
      { dispatch, rejectWithValue },
    ) => {
      dispatch(termsAgreementAccept.request());
      try {
        await apiInstance.terms.termsAgreementAccept({
          id,
        });
        toastMessage.success('Terms Agreement accepted successfully!');
        dispatch(termsAgreementAccept.success());
        dispatch(getPendingTermsAgreement());
      } catch (error: any) {
        dispatch(termsAgreementAccept.failure());
        const message = getThunkError(error);
        toastMessage.error(`Failed to accept Terms Agreement: ${message}`);
        return rejectWithValue(
          error.message || 'An error occurred while accepting Terms Agreement.',
        );
      }
    },
  );

export const getTermsList = createAsyncThunk.withTypes<ThunkApiConfig>()(
  'terms/getTermsList',
  async (_, { dispatch, rejectWithValue }) => {
    dispatch(termsLists.request());
    try {
      const response = await apiInstance.terms.getTermsList();
      const data = response.data.data;

      dispatch(termsLists.success({ data }));
    } catch (error: any) {
      dispatch(termsLists.failure());
      const message = getThunkError(error);
      toastMessage.error(`Failed to load terms: ${message}`);
      return rejectWithValue(
        error.message || 'An error occurred while fetching terms.',
      );
    }
  },
);

export const donwloadTerm = createAsyncThunk<
  DownloadS3UrlTerms,
  number,
  { rejectValue: string }
>('terms/downloadTerm', async (id, { dispatch, rejectWithValue }) => {
  dispatch(donwloadTerms.request());
  try {
    const response = await apiInstance.terms.downloadTerm(id);
    dispatch(donwloadTerms.success());

    return {
      file_s3_url: response.data.data,
    };
  } catch (error: any) {
    dispatch(donwloadTerms.failure());
    const message = getThunkError(error);
    toastMessage.error(`Failed to load terms: ${message}`);
    return rejectWithValue(
      error.message || 'An error occurred while fetching terms.',
    );
  }
});
